import { z } from "zod";

import type { TenantType } from "./tenant";

/**
 * Kanonik sistem rolleri (CLOS-82 `[025]`).
 *
 * İki katman var ve karıştırılmamalı:
 *
 * - **KİMLİK** (`SystemRoleKey`) — makinenin ve veritabanının kullandığı ad. DEĞİŞMEZ:
 *   geçmiş kayıtlar (audit, üyelik) bu anahtara bakar.
 * - **GÖRÜNEN AD** (`SYSTEM_ROLE_NAMES`) — kullanıcıya gösterilen kanonik terim.
 *
 * İkisini ayırmak kartın *"geçmiş rol kimliği korunur"* kriterinin karşılığı: ürün dili
 * ileride değişse bile geçmiş kayıtların işaret ettiği rol aynı rol olarak kalır. Tek bir
 * kolonda birleşselerdi, terminoloji güncellemesi bütün geçmişi yeniden yazmayı gerektirirdi.
 */
export const SYSTEM_ROLE_KEYS = ["org_admin", "standard_user"] as const;
export type SystemRoleKey = (typeof SYSTEM_ROLE_KEYS)[number];

/**
 * Kullanıcıya görünen kanonik adlar (kart: *"rol adları kanonik terminolojiye uyar"*).
 *
 * Ad VERİTABANINDA saklanmıyor, buradan türetiliyor. Satır başına kopyalansaydı iki
 * organizasyonun aynı rolü farklı adlarla görmesi mümkün olurdu; kanonik terim ise
 * organizasyondan bağımsızdır.
 */
export const SYSTEM_ROLE_NAMES: Record<SystemRoleKey, string> = {
  org_admin: "Organizasyon Yöneticisi",
  standard_user: "Standart Kullanıcı",
};

/**
 * Rolün GÖRÜNEN adı: sistem rolünde kanonik terim, özel rolde kullanıcının yazdığı ad.
 *
 * Buraya çıkarıldı çünkü aynı türetme `lib/roleAssignment.ts` ve `lib/invitations.ts`
 * içinde iki kez yazılmıştı; CLOS-79 `[042]` üçüncüsünü ekleyecekti. Üç kopyanın biri
 * güncellenip diğerleri unutulduğunda aynı rol iki ekranda iki farklı adla görünürdü
 * (kriter 5).
 *
 * Bilinmeyen bir `key` geldiğinde anahtarın kendisi dönüyor: boş etiket basmak, hatayı
 * kullanıcıya "rolü yok" diye gösterirdi.
 */
export function roleDisplayName(role: { key: string | null; name: string | null }): string {
  if (role.key) {
    return SYSTEM_ROLE_NAMES[role.key as SystemRoleKey] ?? role.key;
  }
  return role.name ?? "";
}

/**
 * Organizasyon türüne göre VARSAYILAN sistem rol seti (kart kriteri).
 *
 * Kişisel çalışma alanı BOŞ: kart açık — *"CLOS-70 kişisel çalışma alanı Enterprise sistem
 * rolü üretmez."* Kişisel alanda paylaşılacak kimse yok; rol üretmek, hiçbir zaman kimseye
 * atanmayacak satırlar biriktirirdi.
 *
 * İki Enterprise türü BUGÜN aynı seti alıyor ve bu bir tesadüf değil, ölçülen bir karar:
 * kart *"sistem rolü kimliği tenant türünden bağımsız kanonik anlamını korur"* diyor.
 * Ayrım türe göre PERMISSION setinde olacak (CLOS-83 `[026]`); bu yüzden eşleme tür bazlı
 * yazıldı — katalog geldiğinde ayrışma bu tablodan okunur, koda dallanma eklenmez.
 */
export const DEFAULT_SYSTEM_ROLES: Record<TenantType, readonly SystemRoleKey[]> = {
  individual: [],
  enterprise_company: SYSTEM_ROLE_KEYS,
  enterprise_law_firm: SYSTEM_ROLE_KEYS,
  /**
   * Sponsorlu Müvekkil Alanı da BOŞ (CLOS-175 `[122]`).
   *
   * ⚠ Kart: *"alan oluşturma otomatik Müvekkil Temsilcisi, sözleşme paylaşımı, hizmet
   * ilişkisi veya hukuk bürosu kullanıcı erişimi OLUŞTURMAZ."* Sistem rolü üretseydik, alan
   * doğar doğmaz atanmayı bekleyen bir yönetici rolü olurdu ve "kim yönetiyor?" sorusu
   * sponsor lehine bulanıklaşırdı. Temsilcinin yetkisi CLOS-177/178'de tanımlanıyor.
   */
  client_workspace: [],
};

/** Bir çalışma alanı türünün varsayılan sistem rolleri. */
export function defaultSystemRolesFor(type: TenantType): readonly SystemRoleKey[] {
  return DEFAULT_SYSTEM_ROLES[type];
}

/**
 * Kanonik sistem rolü adları — özel rol bunları TAKLİT EDEMEZ (CLOS-85 `[028]`).
 *
 * Karşılaştırma küçük harfe indirilmiş ve kırpılmış hâl üzerinden: "Standart Kullanıcı"
 * ile "standart kullanıcı " aynı adı tarif ediyor. Farklı saymak, kuralı kâğıt üzerinde
 * bırakırdı.
 */
export function impersonatesSystemRole(name: string): boolean {
  const normal = name.trim().toLocaleLowerCase("tr");
  return Object.values(SYSTEM_ROLE_NAMES).some(
    (kanonik) => kanonik.toLocaleLowerCase("tr") === normal,
  );
}

/**
 * Özel rol oluşturma girdisi (CLOS-85 `[028]`).
 *
 * `permissions` BOŞ OLAMAZ: hiçbir yetkisi olmayan bir rol, atandığı kullanıcıyı ürünün
 * dışında bırakır ve kimse bunu isteyerek yapmaz. Boş bir liste neredeyse her zaman
 * "seçmeyi unuttum" demektir.
 */
export const createCustomRoleSchema = z.object({
  name: z
    .string({ error: "Rol adı zorunludur" })
    .trim()
    .min(2, "Rol adı en az 2 karakter olmalıdır")
    .max(80, "Rol adı en fazla 80 karakter olabilir")
    .refine((ad) => !impersonatesSystemRole(ad), "Bu ad sistem rollerine ayrılmıştır"),
  description: z.string().trim().max(500, "Açıklama en fazla 500 karakter olabilir").optional(),
  permissions: z
    .array(z.string({ error: "Yetki anahtarı metin olmalıdır" }))
    .min(1, "En az bir yetki seçilmelidir"),
});
export type CreateCustomRoleInput = z.infer<typeof createCustomRoleSchema>;

/**
 * Özel rol düzenleme girdisi (CLOS-86 `[029]`).
 *
 * Yetki seti TAM gönderiliyor, fark değil: istemcinin hesapladığı bir fark, iki yönetici
 * aynı anda düzenlediğinde sessizce yanlış sonuca varır. Sunucu farkı kendisi çıkarıyor.
 */
export const updateCustomRoleSchema = z.object({
  name: z
    .string({ error: "Rol adı zorunludur" })
    .trim()
    .min(2, "Rol adı en az 2 karakter olmalıdır")
    .max(80, "Rol adı en fazla 80 karakter olabilir")
    .refine((ad) => !impersonatesSystemRole(ad), "Bu ad sistem rollerine ayrılmıştır"),
  description: z.string().trim().max(500, "Açıklama en fazla 500 karakter olabilir").optional(),
  permissions: z
    .array(z.string({ error: "Yetki anahtarı metin olmalıdır" }))
    .min(1, "En az bir yetki seçilmelidir"),
  /**
   * RİSKLİ değişiklik için AÇIK ONAY (kart kriteri).
   *
   * Varsayılan `false`: onay, kullanıcının farkında olmadan verebileceği bir şey olmamalı.
   * Riskin ne olduğu sunucuda tanımlı — istemcinin karar vermesi, arayüzü atlayan bir
   * istekte onayın hiç sorulmaması demekti.
   */
  confirmRiskyChange: z.boolean().default(false),
});
export type UpdateCustomRoleInput = z.infer<typeof updateCustomRoleSchema>;

/**
 * Özel rol çoğaltma girdisi (CLOS-87 `[030]`).
 *
 * Yetki listesi ALINMIYOR: kaynak rolün seti kopyalanıyor ve sunucu onu güncel katalogla
 * yeniden değerlendiriyor. İstemciden liste alsaydık bu bir "çoğaltma" değil, gizlenmiş
 * bir "oluşturma" olurdu.
 */
export const duplicateCustomRoleSchema = z.object({
  name: z
    .string({ error: "Rol adı zorunludur" })
    .trim()
    .min(2, "Rol adı en az 2 karakter olmalıdır")
    .max(80, "Rol adı en fazla 80 karakter olabilir")
    .refine((ad) => !impersonatesSystemRole(ad), "Bu ad sistem rollerine ayrılmıştır"),
  description: z.string().trim().max(500, "Açıklama en fazla 500 karakter olabilir").optional(),
});
export type DuplicateCustomRoleInput = z.infer<typeof duplicateCustomRoleSchema>;

/**
 * Üyeliğe veya bekleyen davete ROL ATAMA girdisi (CLOS-89 `[039]`).
 *
 * Yalnız rolün KİMLİĞİ alınıyor; yetki listesi değil. Liste alınsaydı bu bir atama değil,
 * o üyeliğe özel gizli bir rol tanımlamak olurdu ve *"kullanıcı yalnız bir aktif role
 * sahip olur"* kuralı anlamını yitirirdi.
 */
export const assignRoleSchema = z.object({
  roleId: z.uuid("Geçerli bir rol seçilmelidir"),
});
export type AssignRoleInput = z.infer<typeof assignRoleSchema>;

/**
 * KALITIM BAĞI (CLOS-1211 — Final Kanonik v1.0 §7.3).
 *
 * ⚠ `null` bir hata değil, BİR KOMUT: "bağı kaldır". Ayrı bir uç açsaydık (`DELETE
 * /parent`) aynı kararın iki adresi olurdu ve biri gün gelip diğerinden ayrışırdı.
 *
 * ⚠ Bu bir KOPYA DEĞİL canlı bağ: ata rolün yetkisi kaldırıldığında devralan da kaybeder.
 * Kanon §7.4 "mevcut rolden başla" varsayılanını ayrıca **one-time copy** diye tanımlıyor —
 * o başka bir yolun (çoğaltma) işi ve bu şema onunla karıştırılamaz.
 */
export const setRoleParentSchema = z.object({
  parentRoleId: z.uuid("Geçerli bir rol seçilmelidir").nullable(),
});
export type SetRoleParentInput = z.infer<typeof setRoleParentSchema>;

/**
 * AUTHORITY TIER (CLOS-1211 — §7.2).
 *
 * ⚠ Tier YETKİ VERMEZ, yalnız kimin kimden devralabileceğini sıralar. Üst sınır bir ürün
 * kararı değil bir akıl sağlığı kemeri: sınırsız bırakılsaydı arayüzde sıralanamayacak
 * kadar seyrek bir sayı uzayı doğardı.
 */
export const setRoleTierSchema = z.object({
  authorityTier: z
    .number({ error: "Yetki seviyesi zorunludur" })
    .int("Yetki seviyesi tam sayı olmalıdır")
    .min(0, "Yetki seviyesi 0'dan küçük olamaz")
    .max(100, "Yetki seviyesi en fazla 100 olabilir"),
});
export type SetRoleTierInput = z.infer<typeof setRoleTierSchema>;

/** Bir düzenlemenin yetki farkı — kart: *"önceki ve yeni permission farkı"*. */
export type PermissionDiff = { added: string[]; removed: string[]; unchanged: string[] };

export function diffPermissions(
  before: readonly string[],
  after: readonly string[],
): PermissionDiff {
  const oncekiler = new Set(before);
  const sonrakiler = new Set(after);
  return {
    added: [...sonrakiler].filter((k) => !oncekiler.has(k)).sort(),
    removed: [...oncekiler].filter((k) => !sonrakiler.has(k)).sort(),
    unchanged: [...sonrakiler].filter((k) => oncekiler.has(k)).sort(),
  };
}
